import React, { useEffect, useState } from "react";
import AdminNavbar from "./AdminNavbar";
import attendanceAPI from "../api/attendanceAPI";
import Swal from "sweetalert2";
import { FaMapMarkerAlt, FaExternalLinkAlt } from "react-icons/fa";

// ── IST formatter helpers ─────────────────────────────────────────────────────
const toIST = (timeStr) => {
  if (!timeStr) return null;

  // Already a plain time string like "09:30 AM" or "14:30" — stored as IST, return as-is
  if (/^\d{1,2}:\d{2}/.test(timeStr) && timeStr.length <= 8) {
    return timeStr;
  }

  // Full ISO string or timestamp — convert to IST
  const date = new Date(timeStr);
  if (isNaN(date)) return timeStr; // fallback: return original string

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    hour:     '2-digit',
    minute:   '2-digit',
    hour12:   true,
  }).format(date);
};

const toISTDate = (dateStr) => {
  if (!dateStr) return null;

  // Plain date string "YYYY-MM-DD" — parse manually to avoid UTC midnight shift
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split('-').map(Number);
    return `${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}/${y}`;
  }

  // Full ISO timestamp — convert to IST
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;

  return new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day:      '2-digit',
    month:    '2-digit',
    year:     'numeric',
  }).format(date);
};

// ── Location status badge ─────────────────────────────────────────────────────
const LocationBadge = ({ status, distance }) => {
  const styles = {
    onsite:      "bg-green-100 text-green-700",
    remote:      "bg-blue-100 text-blue-700",
    verified:    "bg-green-100 text-green-700",
    outside:     "bg-red-100 text-red-700",
    not_checked: "bg-gray-100 text-gray-500",
  };
  const labels = {
    onsite:      "On-site ✓",
    remote:      "Remote ✓",
    verified:    "Verified ✓",
    outside:     "Outside ✗",
    not_checked: "—",
  };
  return (
    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${styles[status] || styles.not_checked}`}>
      {labels[status] || "—"}
      {distance != null && status !== "not_checked" && (
        <span className="ml-1 font-normal">({distance}m)</span>
      )}
    </span>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
const AdminAttendance = () => {
  const [records, setRecords]             = useState([]);
  const [searchQuery, setSearchQuery]     = useState("");
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });

  useEffect(() => {
    attendanceAPI
      .get("/")
      .then((res) => setRecords(res.data))
      .catch((err) => console.error(err));
  }, []);

  // ── Filters ───────────────────────────────────────────────────────────────
  const filteredRecords = records
    .filter((rec) => {
      const s = searchQuery.toLowerCase();
      return (
        rec.username?.toLowerCase().includes(s) ||
        rec.date?.includes(searchQuery) ||
        rec.workMode?.toLowerCase().includes(s) ||
        rec.status?.toLowerCase().includes(s) ||
        rec.role?.toLowerCase().includes(s)
      );
    })
    .filter((rec) => {
      if (!selectedMonth) return true;
      return rec.date?.slice(0, 7) === selectedMonth;
    });

  // ── Monthly hours helper ──────────────────────────────────────────────────
  const getUserMonthlyHours = (username) => {
    let total = 0;
    filteredRecords.forEach((rec) => {
      if (rec.username === username && rec.status?.toLowerCase() === "present") {
        if (rec.totalHours) {
          const val = String(rec.totalHours).trim();
          if (val.includes(":")) {
            const [h, m] = val.split(":").map(Number);
            total += h + m / 60;
          } else {
            total += parseFloat(val.replace(/[^\d.]/g, "")) || 0;
          }
        }
      }
    });
    return total;
  };

  // ── View hours popup ──────────────────────────────────────────────────────
  const handleViewHours = (username) => {
    const total = getUserMonthlyHours(username);
    const monthName = new Intl.DateTimeFormat('en-IN', {
      timeZone: 'Asia/Kolkata',
      month:    'long',
      year:     'numeric',
    }).format(new Date(selectedMonth + '-02')); // use 2nd to avoid UTC rollback to prev month

    Swal.fire({
      title: `${username}'s Working Hours`,
      html: `
        <div style="font-size:1.1rem;color:#4b0082;">
          <strong>Month:</strong> ${monthName}<br/>
          <strong>Total Working Hours:</strong> ${total.toFixed(2)} hrs
        </div>`,
      icon:               'info',
      confirmButtonColor: '#6b21a8',
    });
  };

  // ── View location details popup ───────────────────────────────────────────
  const handleViewLocation = (rec) => {
    const sessions    = rec.sessions || [];
    const hasLocation = sessions.some(s => s.currentLat != null);

    if (!hasLocation && !rec.lat) {
      Swal.fire({
        title: 'Location Info',
        html:  `<p class="text-gray-500">No location data recorded for this attendance.</p>`,
        icon:  'info',
        confirmButtonColor: '#6b21a8',
      });
      return;
    }

    const sessionRows = sessions.map((s, i) => {
      const lat  = s.currentLat ?? rec.lat;
      const lng  = s.currentLng ?? rec.lng;
      const addr = s.currentAddress || (lat ? `${lat?.toFixed(5)}, ${lng?.toFixed(5)}` : '—');

      const mapLink = lat && lng
        ? `<a href="https://maps.google.com/?q=${lat},${lng}" target="_blank"
             style="color:#6b21a8;text-decoration:underline;">
             View on Map ↗
           </a>`
        : '—';

      const statusLabel = {
        onsite:      '✅ On-site',
        remote:      '✅ Remote',
        outside:     '❌ Outside',
        not_checked: '—',
      }[s.locationStatus] || '—';

      const loginIST  = toIST(s.loginTime)  || '—';
      const logoutIST = s.logoutTime ? (toIST(s.logoutTime) || s.logoutTime) : 'active';

      return `
        <div style="border:1px solid #e9d5ff;border-radius:8px;padding:10px;margin-bottom:8px;text-align:left;background:#faf5ff;">
          <div style="font-weight:600;color:#6b21a8;margin-bottom:4px;">
            Session ${i + 1} — ${loginIST} → ${logoutIST}
          </div>
          <div style="font-size:0.85rem;color:#374151;">
            <div>📍 <strong>Address:</strong> ${addr}</div>
            <div>📏 <strong>Distance from base:</strong> ${s.distanceFromBase != null ? s.distanceFromBase + 'm' : '—'}</div>
            <div>🔖 <strong>Status:</strong> ${statusLabel}</div>
            <div style="margin-top:4px;">${mapLink}</div>
          </div>
        </div>`;
    }).join('');

    Swal.fire({
      title:              `${rec.username} — Location Details`,
      html:               sessionRows || '<p>No sessions found.</p>',
      confirmButtonColor: '#6b21a8',
      width:              600,
    });
  };

  // ── CSV download ──────────────────────────────────────────────────────────
  const downloadCSV = () => {
    const headers = [
      "Date","Name","Company","Role","Team","Work Mode",
      "Login Time","Logout Time","Location Address","Distance (m)","Location Status",
      "Total Hours","Overtime","Status",
    ];

    const rows = filteredRecords.flatMap((rec) => {
      const isAbsent = rec.status === "Absent";
      if (rec.sessions?.length > 0 && !isAbsent) {
        return rec.sessions.map((s) => [
          rec.date ? `'${rec.date}` : "N/A",
          rec.username, rec.company || "N/A", rec.role, rec.team, rec.workMode || "N/A",
          toIST(s.loginTime)  || s.loginTime  || "N/A",
          toIST(s.logoutTime) || s.logoutTime || "N/A",
          s.currentAddress || (rec.lat ? `${rec.lat?.toFixed(5)},${rec.lng?.toFixed(5)}` : "N/A"),
          s.distanceFromBase != null ? s.distanceFromBase : "N/A",
          s.locationStatus || "N/A",
          rec.totalHours || "N/A",
          rec.overtime   || "N/A",
          rec.status,
        ]);
      } else {
        return [[
          rec.date ? `'${rec.date}` : "N/A",
          rec.username, rec.company || "N/A", rec.role, rec.team, rec.workMode || "N/A",
          "N/A","N/A","N/A","N/A","N/A",
          isAbsent ? "N/A" : rec.totalHours || "N/A",
          isAbsent ? "N/A" : rec.overtime   || "N/A",
          rec.status,
        ]];
      }
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.map((v) => `"${v}"`).join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href  = URL.createObjectURL(blob);
    link.setAttribute("download", "attendance_report.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <AdminNavbar />
      <div className="min-h-screen bg-gradient-to-br from-purple-100 to-pink-100 p-8 pt-20">
        <h2 className="text-3xl font-bold font-mono text-purple-700 mb-2 mt-4 text-center">
          Attendance Report
        </h2>

        {/* ── Filters Row ──────────────────────────────────────────────────── */}
        <div className="bg-white shadow-md rounded-xl p-4 mb-6">
          <div className="max-w-8xl mx-auto flex flex-col lg:flex-row justify-between items-center gap-4 mb-4">

            <input
              type="text"
              placeholder="Search by name, date, role, work mode, status…"
              className="px-4 py-2 border rounded-lg w-full lg:w-1/3"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 w-full lg:w-auto">
              <label className="text-purple-700 font-semibold whitespace-nowrap">Select Month:</label>
              <input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="border rounded-lg p-2 w-full sm:w-auto"
              />
            </div>

            <button
              onClick={() => {
                Swal.fire({
                  title: "Download Report?",
                  text:  "Download this attendance report as CSV?",
                  icon:  "question",
                  showCancelButton:    true,
                  confirmButtonColor:  "#6b21a8",
                  cancelButtonColor:   "#d33",
                  confirmButtonText:   "Yes",
                }).then((res) => { if (res.isConfirmed) downloadCSV(); });
              }}
              className="bg-purple-600 text-white px-6 py-2 rounded-lg hover:bg-purple-700 w-full sm:w-auto"
            >
              Download CSV
            </button>
          </div>

          {/* ── Table ──────────────────────────────────────────────────────── */}
          <div className="md:max-h-[450px] max-h-[500px] overflow-auto">
            <table className="min-w-full table-auto text-sm">
              <thead className="bg-purple-100 text-purple-700">
                <tr>
                  {[
                    "Date","Profile","Name","Company","Role","Team",
                    "Work Mode","Login","Logout","Location","Total Hrs","Overtime","Status","Actions",
                  ].map((h) => (
                    <th key={h} className="py-3 px-3 sticky top-0 bg-purple-100 text-left whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>

              <tbody className="text-gray-700">
                {filteredRecords.map((rec, idx) => (
                  <tr key={idx} className="border-t hover:bg-purple-50 transition">

                    {/* Date */}
                    <td className="py-3 px-3 whitespace-nowrap">
                      {toISTDate(rec.date) || rec.date}
                    </td>

                    {/* Profile image */}
                    <td className="py-3 px-3">
                      <img
                        src={rec.profileImage}
                        alt="profile"
                        className="w-9 h-9 rounded-full object-cover border border-purple-200"
                      />
                    </td>

                    {/* Name */}
                    <td className="py-3 px-3 font-semibold text-purple-700">{rec.username}</td>

                    {/* Company */}
                    <td className="py-3 px-3">{rec.company || "—"}</td>

                    {/* Role */}
                    <td className="py-3 px-3">{rec.role}</td>

                    {/* Team */}
                    <td className="py-3 px-3">{rec.team || "—"}</td>

                    {/* Work Mode */}
                    <td className="py-3 px-3">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        rec.workMode === "Remote"
                          ? "bg-blue-100 text-blue-700"
                          : "bg-gray-100 text-gray-600"
                      }`}>
                        {rec.workMode || "—"}
                      </span>
                    </td>

                    {/* Login times */}
                    <td className="py-3 px-3">
                      {rec.sessions?.length > 0
                        ? rec.sessions.map((s, i) => (
                            <div key={i} className="text-xs">
                              {toIST(s.loginTime) || s.loginTime || "—"}
                            </div>
                          ))
                        : "—"}
                    </td>

                    {/* Logout times */}
                    <td className="py-3 px-3">
                      {rec.sessions?.length > 0
                        ? rec.sessions.map((s, i) => (
                            <div key={i} className="text-xs">
                              {s.logoutTime
                                ? toIST(s.logoutTime) || s.logoutTime
                                : <span className="text-orange-500 text-xs">active</span>}
                            </div>
                          ))
                        : "—"}
                    </td>

                    {/* Location column */}
                    <td className="py-3 px-3">
                      {rec.sessions?.length > 0 ? (
                        <div className="space-y-1">
                          {rec.sessions.map((s, i) => {
                            const lat = s.currentLat ?? rec.lat;
                            const lng = s.currentLng ?? rec.lng;
                            return (
                              <div key={i} className="flex items-center gap-1 flex-wrap">
                                <LocationBadge
                                  status={s.locationStatus || "not_checked"}
                                  distance={s.distanceFromBase}
                                />
                                {lat && lng && (
                                  <a
                                    href={`https://maps.google.com/?q=${lat},${lng}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-purple-500 hover:text-purple-700 text-xs flex items-center gap-0.5"
                                    title="View on Google Maps"
                                  >
                                    <FaMapMarkerAlt />
                                    <FaExternalLinkAlt className="text-[10px]" />
                                  </a>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </td>

                    {/* Total hours */}
                    <td className="py-3 px-3 font-medium">{rec.totalHours || "—"}</td>

                    {/* Overtime */}
                    <td className="py-3 px-3 text-orange-600 font-medium">
                      {rec.overtime && rec.overtime !== "00:00" ? rec.overtime : "—"}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-3">
                      <span className={`font-semibold ${
                        rec.status === "Present" ? "text-green-600" : "text-red-500"
                      }`}>
                        {rec.status}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-3">
                      <div className="flex flex-col gap-1">
                        <button
                          onClick={() => handleViewHours(rec.username)}
                          className="bg-purple-600 text-white px-3 py-1 rounded text-xs hover:bg-purple-700 whitespace-nowrap"
                        >
                          Hours
                        </button>
                        <button
                          onClick={() => handleViewLocation(rec)}
                          className="bg-blue-600 text-white px-3 py-1 rounded text-xs hover:bg-blue-700 flex items-center gap-1 whitespace-nowrap"
                        >
                          <FaMapMarkerAlt /> Location
                        </button>
                      </div>
                    </td>

                  </tr>
                ))}

                {filteredRecords.length === 0 && (
                  <tr>
                    <td colSpan="14" className="text-center py-6 text-gray-500">
                      No attendance records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminAttendance;